import React, { useEffect } from 'react';
import { observer, inject } from 'mobx-react';
import { UserOutlined, EditOutlined, ClockCircleOutlined } from '@ant-design/icons';
import {
  ActivityHistoryWrapper,
  ActivityItem,
  ActivityIcon,
  ActivityDetails,
  UserIcon,   // Chúng ta đang dùng styled-component cho icon này
  EditIcon,   // styled-component cho EditIcon
  ClockIcon,   // styled-component cho ClockIcon
  TableWrapper,
  TableCell
} from './ActivityHistoryStyled'; // Import styled-component

// Hàm định dạng processed_date
const formatDate = (dateString) => {
  const date = new Date(dateString);

  // Lấy giờ, phút, ngày, tháng, năm từ đối tượng Date
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Tháng tính từ 0
  const year = date.getFullYear();

  // Trả về định dạng "HH:mm - DD/MM/YYYY"
  return `${hours}:${minutes} - ${day}/${month}/${year}`;
};

const translateUrgencyLevel = (urgencyLevel) => {
  switch (urgencyLevel) {
    case "001":
      return "Khẩn";
    case "002":
      return "Hỏa tốc";
    case "003":
      return "Hỏa tốc hẹn giờ";
    default:
      return "Không xác định";
  }
};

const formatPermission = (permission) => {
  switch (permission) {
    case 'VIEW':
      return 'Phối hợp / Theo dõi';
    case 'PIC':
      return 'Lãnh đạo xử lý';
    case 'COOR':
      return 'Người xử lý';
    default:
      return permission; // Trả về giá trị permission gốc nếu không có trong danh sách
  }
};

// Hàm chuyển đổi JSON thành văn xuôi
const formatDataBefore = (data) => {
  try {
    if (data === null || data === undefined) {
      return 'Không có dữ liệu';
    }

    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    if (typeof data === 'object' && data !== null) {
      let result = '';

      // Xử lý danh sách old_persons
      if (data.old_persons && Array.isArray(data.old_persons) && data.old_persons.length > 0) {
        // Không còn ignoreList, trực tiếp sử dụng old_persons
        const personList = data.old_persons.map(person => {
          const permissionDescription = formatPermission(person.permission);
          return `• ${permissionDescription}: ${person.nameUppercase}`; // Định dạng từng cá nhân
        });

        // Kết hợp tiêu đề với danh sách cá nhân
        result = `Người đã thêm:<br>${personList.join('<br>')}`; // Kết hợp với tiêu đề "Người đã thêm"
      }

      // Xử lý remove_persons nếu có
      if (data.remove_persons && typeof data.remove_persons === 'object') {
        let removePersonsData = [];

        const { bookGroupName, bookName, documentNumber, incomingNumber, title, outgoingDocumentDate, dateIssued, signer, urgencyLevel, attachments, authorityIssuedName, } = data.remove_persons;

        if (bookGroupName) {
          removePersonsData.push(`• Nhóm số văn bản : ${bookGroupName}`);
        }
        if (bookName) {
          removePersonsData.push(`• Số văn bản: ${bookName}`);
        }
        if (documentNumber) {
          removePersonsData.push(`• Số hiệu: ${documentNumber}`);
        }
        if (incomingNumber) {
          removePersonsData.push(`• Số đến: ${incomingNumber}`);
        }
        if (title) {
          removePersonsData.push(`• Trích yếu: ${title}`);
        }
        if (outgoingDocumentDate?.dateTime?.date) {
          const { year, month, day } = outgoingDocumentDate.dateTime.date;
          removePersonsData.push(`• Ngày đến: ${year}-${month}-${day}`);
        }
        if (dateIssued?.dateTime?.date) {
          const { year, month, day } = dateIssued.dateTime.date;
          removePersonsData.push(`• Ngày văn bản: ${year}-${month}-${day}`);
        }
        if (signer) {
          removePersonsData.push(`• Người ký: ${signer}`);
        }
        if (urgencyLevel) {
          const urgencyDescription = translateUrgencyLevel(urgencyLevel);
          removePersonsData.push(`• Độ khẩn: ${urgencyDescription}`);
        }
        if (attachments) {
          removePersonsData.push(`• Tài liệu đính kèm: ${attachments.join(", ")}`);
        }
        if (authorityIssuedName) {
          removePersonsData.push(`• Cơ quan ban hành: ${authorityIssuedName}`);
        }

        if (removePersonsData.length > 0) {
          result += (result.length > 0 ? ' | ' : '') + `${removePersonsData.join('<br>')}`;
        }
      }

      return result || 'Không có dữ liệu';
    }

    return data;
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return 'Dữ liệu không hợp lệ';
  }
};


// Hàm chuyển đổi JSON thành văn xuôi
const formatDataAfter = (data) => {
  try {
    if (data === null || data === undefined) {
      return 'Không có dữ liệu';
    }

    // Nếu data là chuỗi JSON, phân tích nó
    if (typeof data === 'string') {
      data = JSON.parse(data);
    }

    if (typeof data === 'object' && data !== null) {
      let result = '';

      // Xử lý danh sách add_persons
      if (data.add_persons && Array.isArray(data.add_persons) && data.add_persons.length > 0) {
        const addedPersons = data.add_persons.map(person => {
          const permissionDescription = formatPermission(person.permission);
          return `• ${permissionDescription}: ${person.nameUppercase}`;
        }).join('<br>'); // Tạo dòng mới cho mỗi người
        result += `Người đã thêm:<br>${addedPersons}`;
      }

      // Xử lý danh sách remove_persons
      if (data.remove_persons && Array.isArray(data.remove_persons) && data.remove_persons.length > 0) {
        const removedPersons = data.remove_persons.map(person => {
          const permissionDescription = formatPermission(person.permission);
          return `• ${permissionDescription}: ${person.nameUppercase}`;
        }).join('<br>'); // Tạo dòng mới cho mỗi người
        result += (result.length > 0 ? '<br><br>' : '') + `Người đã bỏ:<br>${removedPersons}`;
      }
      // Xử lý đối tượng old_persons
      if (data.old_persons && typeof data.old_persons === 'object') {
        const { bookGroupName, bookName, documentNumber, incomingNumber, title, outgoingDocumentDate, dateIssued, signer, urgencyLevel, attachmentChanges, authorityIssuedName, } = data.old_persons;

        // Kiểm tra từng giá trị và chỉ thêm vào nếu có dữ liệu
        let oldPersonsData = [];

        if (bookGroupName) {
          oldPersonsData.push(`• Nhóm số văn bản : ${bookGroupName}`);
        }
        if (bookName) {
          oldPersonsData.push(`• Số văn bản: ${bookName}`);
        }
        if (documentNumber) {
          oldPersonsData.push(`• Số hiệu: ${documentNumber}`);
        }
        if (incomingNumber) {
          oldPersonsData.push(`• Số đến: ${incomingNumber}`);
        }
        if (title) {
          oldPersonsData.push(`• Trích yếu: ${title}`);
        }
        if (outgoingDocumentDate?.dateTime?.date) {
          const { year, month, day } = outgoingDocumentDate.dateTime.date;
          oldPersonsData.push(`• Ngày đến: ${year}-${month}-${day}`);
        }
        if (dateIssued?.dateTime?.date) {
          const { year, month, day } = dateIssued.dateTime.date;
          oldPersonsData.push(`• Ngày văn bản: ${year}-${month}-${day}`);
        }
        if (signer) {
          oldPersonsData.push(`• Người ký: ${signer}`);
        }
        if (urgencyLevel) {
          const urgencyDescription = translateUrgencyLevel(urgencyLevel);
          oldPersonsData.push(`• Độ khẩn: ${urgencyDescription}`);
        }
        if (attachmentChanges && attachmentChanges.length > 0) {
          oldPersonsData.push(`• Tài liệu đính kèm đã sửa: ${attachmentChanges.join(", ")}`);
        } else {
          oldPersonsData.push("• Tài liệu đính kèm: Không có thay đổi");
        }
        if (authorityIssuedName) {
          oldPersonsData.push(`• Cơ quan ban hành: ${authorityIssuedName}`);
        }

        // Chỉ nối các trường có dữ liệu
        if (oldPersonsData.length > 0) {
          result += (result.length > 0 ? ' | ' : '') + `${oldPersonsData.join('<br>')}`;
        }
      }

      // Nếu không có thông tin gì, trả về 'Không có thay đổi'
      return result || 'Không có thay đổi';
    }

    // Nếu không phải đối tượng, trả về dữ liệu như nó là
    return data;
  } catch (e) {
    console.error('Error parsing JSON:', e);
    return 'Dữ liệu không hợp lệ';
  }
};


const capitalizeFirstLetters = (str) => {
  if (typeof str !== 'string') return ''; // Trả về chuỗi rỗng nếu đầu vào không phải là chuỗi
  return str
    .toLowerCase()
    .split(' ')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
};

const ActivityHistory = ({ listHistoryLogStore, documentCode }) => {
  // Lấy các dữ liệu và method từ store
  const { historyLists, getLists, isLoading } = listHistoryLogStore;

  useEffect(() => {
    if (documentCode) {
      const respone = getLists(documentCode);
      // console.log(respone);
    }
  }, [documentCode]);

  const actionMapping = {
    CREATE: 'Tạo mới',
    EDIT_ASSIGNEE: 'Thêm người',
    UPDATE: 'Chỉnh sửa',
  };

  const renderActivityItem = (activity, index) => {
    const userUppercase = activity.user.name_uppercase || '';
    const formattedUserUppercase = capitalizeFirstLetters(userUppercase);
    const formattedDataBefore = formatDataBefore(activity.data_before);
    const formattedDataAfter = formatDataAfter(activity.data_after);

    return (
      <ActivityItem key={index}>
        <ul>
          <li>
            <p><b>Đã sửa</b> lúc {formatDate(activity.processed_date)} bởi <b>{formattedUserUppercase}</b></p>
          </li>
        </ul>
        <TableWrapper>
          <TableCell className="title">Trường dữ liệu</TableCell>
          <TableCell className="title">Trước thay đổi</TableCell>
          <TableCell className="title">Sau thay đổi</TableCell>
          <TableCell>{actionMapping[activity.action] || 'N/A'}</TableCell>
          <TableCell dangerouslySetInnerHTML={{ __html: formattedDataBefore || 'N/A' }} />
          <TableCell dangerouslySetInnerHTML={{ __html: formattedDataAfter || 'N/A' }} />
        </TableWrapper>
      </ActivityItem>
    );
  };


  return (
    <ActivityHistoryWrapper>
      {isLoading ? (
        <p>Đang tải...</p> // Hiển thị thông báo khi đang tải dữ liệu
      ) : historyLists?.length === 0 ? (
        <p>Không có lịch sử hoạt động.</p> // Thông báo khi không có dữ liệu
      ) : (
        <div className="activity-list">
          {historyLists
            ?.sort((a, b) => {
              const dateA = new Date(a.processed_date).getTime();
              const dateB = new Date(b.processed_date).getTime();
              return dateB - dateA; // Sắp xếp theo thứ tự mới nhất đến cũ nhất
            })
            .map(renderActivityItem)} {/* Hiển thị danh sách lịch sử */}
        </div>
      )}
    </ActivityHistoryWrapper>
  );
};

// Inject store 'listHistoryLogStore' và sử dụng observer để render lại khi store thay đổi
export default inject('listHistoryLogStore')(observer(ActivityHistory));
